import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlansAdminService, Plan } from "../services/plansAdminService";
import { PlanEditorDialog } from "./PlanEditorDialog";
import { WaitlistsPanel } from "./WaitlistsPanel";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Switch } from "@/shared/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/shared/components/ui/alert-dialog";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Plus, Pencil, Trash2 } from "lucide-react";

function formatPrice(usd?: number, brl?: number) {
  const parts: string[] = [];
  if (usd != null) parts.push(`$${usd}`);
  if (brl != null) parts.push(`R$${brl}`);
  return parts.join(" / ") || "—";
}

function PlansTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Plan | null>(null);

  const { data: plans, isLoading } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: () => PlansAdminService.getPlans(),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => PlansAdminService.togglePlan(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-plans"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => PlansAdminService.deletePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
      setDeleteTarget(null);
    },
  });

  const openCreate = () => {
    setEditingPlan(undefined);
    setEditorOpen(true);
  };

  const openEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setEditorOpen(true);
  };

  const sorted = [...(plans ?? [])].sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={openCreate} size="sm">
          <Plus className="h-4 w-4 mr-1" /> {t("settings.plansAdmin.list.newPlan")}
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">#</TableHead>
              <TableHead>{t("settings.plansAdmin.list.name")}</TableHead>
              <TableHead>{t("settings.plansAdmin.list.slug")}</TableHead>
              <TableHead>{t("settings.plansAdmin.list.price")}</TableHead>
              <TableHead>{t("settings.plansAdmin.list.billing")}</TableHead>
              <TableHead>{t("settings.plansAdmin.list.permissionList")}</TableHead>
              <TableHead className="w-20">{t("settings.plansAdmin.list.active")}</TableHead>
              <TableHead className="w-20 text-right">{t("settings.plansAdmin.list.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  {t("settings.plansAdmin.list.empty")}
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell className="text-muted-foreground text-xs">{plan.sort_order ?? "—"}</TableCell>
                  <TableCell className="font-medium">{plan.name}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">{plan.slug}</code>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatPrice(plan.content?.price_usd, plan.content?.price_brl)}
                  </TableCell>
                  <TableCell className="text-sm capitalize">
                    {plan.content?.billing_cycle ?? "—"}
                  </TableCell>
                  <TableCell>
                    {plan.content?.permission_list ? (
                      <Badge variant="outline" className="text-xs font-mono">
                        {plan.content.permission_list}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={plan.is_active}
                      onCheckedChange={() => toggleMutation.mutate(plan.id)}
                      disabled={toggleMutation.isPending}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(plan)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(plan)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PlanEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        plan={editingPlan}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("settings.plansAdmin.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.plansAdmin.delete.description", { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("settings.plansAdmin.delete.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? t("settings.plansAdmin.delete.deleting") : t("settings.plansAdmin.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function PlansSettings() {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans">{t("settings.plansAdmin.tabs.plans")}</TabsTrigger>
          <TabsTrigger value="waitlists">{t("settings.plansAdmin.tabs.waitlists")}</TabsTrigger>
        </TabsList>
        <TabsContent value="plans" className="mt-4">
          <PlansTab />
        </TabsContent>
        <TabsContent value="waitlists" className="mt-4">
          <WaitlistsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
